import React, { useState, useEffect, useContext } from 'react';
import Layout from '../../components/Layout';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stack
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import axios from 'axios';

const ActionCard = ({ icon, title, description, cta, onClick, gradient }) => (
  <Card
    elevation={0}
    sx={{
      position: 'relative',
      height: '100%',
      borderRadius: 3,
      overflow: 'hidden',
      background: gradient,
      color: 'common.white',
      p: 0,
      boxShadow:
        '0 10px 30px rgba(16, 24, 40, 0.10), inset 0 1px 0 rgba(255,255,255,0.12)',
      transition: 'transform .25s ease, box-shadow .25s ease',
      '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow:
          '0 20px 50px rgba(16, 24, 40, 0.18), inset 0 1px 0 rgba(255,255,255,0.12)'
      }
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(1200px 300px at -10% -20%, rgba(255,255,255,.15) 0%, rgba(255,255,255,0) 60%), radial-gradient(1200px 300px at 120% 120%, rgba(255,255,255,.10) 0%, rgba(255,255,255,0) 60%)'
      }}
    />
    <CardContent sx={{ position: 'relative', p: { xs: 3, md: 4 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            display: 'grid',
            placeItems: 'center',
            borderRadius: '14px',
            backgroundColor: 'rgba(255,255,255,.18)',
            backdropFilter: 'blur(4px)'
          }}
        >
          {icon}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ opacity: 0.95, mb: 3 }}>
        {description}
      </Typography>
      <Button
        size="large"
        variant="contained"
        endIcon={<ArrowOutwardIcon />}
        onClick={onClick}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 700,
          px: 2.5,
          backgroundColor: 'rgba(255,255,255,.22)',
          color: 'common.white',
          '&:hover': { backgroundColor: 'rgba(255,255,255,.30)' }
        }}
      >
        {cta}
      </Button>
    </CardContent>
  </Card>
);

const PartnerHome = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartnerInfo = async () => {
      try {
        const response = await axios.get('/api/partners/current-partner-info');
        setPartnerInfo(response.data);
      } catch (error) {
        console.error('Error fetching partner info:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'PARTNER') {
      fetchPartnerInfo();
    } else {
      setLoading(false);
    }
  }, [user]);

  const getWelcomeMessage = () => {
    if (loading) return 'Welcome';
    
    if (partnerInfo?.referringPartner) {
      return `Welcome to ${partnerInfo.referringPartner.name}`;
    }
    
    return 'Welcome';
  };

  return (
    <Layout title="Partner Portal">
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, md: 10 },
          background:
            'linear-gradient(180deg, rgba(2,6,23,0) 0%, rgba(2,6,23,.04) 100%)'
        }}
      >
        {/* Decorative background */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -160,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 900,
              height: 900,
              background:
                'radial-gradient(closest-side, rgba(59,130,246,.18), transparent), radial-gradient(closest-side, rgba(147,51,234,.16), transparent)',
              filter: 'blur(60px)'
            }}
          />
        </Box>

        <Container maxWidth="lg">
          {/* Hero */}
          <Box
            sx={{
              mb: { xs: 6, md: 8 },
              textAlign: 'center',
              position: 'relative'
            }}
          >
            {/* Partner Logo and Welcome Message */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={{ xs: 2, sm: 3 }}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 2 }}
            >
              {partnerInfo?.referringPartner?.logo && (
                <Box
                  component="img"
                  src={partnerInfo.referringPartner.logo}
                  alt={`${partnerInfo.referringPartner.name} logo`}
                  sx={{
                    height: { xs: 60, sm: 80, md: 100 },
                    maxWidth: { xs: '200px', sm: '250px', md: '300px' },
                    width: 'auto',
                    objectFit: 'contain',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    backgroundColor: 'background.paper',
                    p: { xs: 1, sm: 1.5 },
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                />
              )}
              {partnerInfo?.partnerLogo && (
                <Box
                  component="img"
                  src={partnerInfo.partnerLogo}
                  alt="Your partner logo"
                  sx={{
                    height: { xs: 60, sm: 80, md: 100 },
                    maxWidth: { xs: '200px', sm: '250px', md: '300px' },
                    width: 'auto',
                    objectFit: 'contain',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    backgroundColor: 'background.paper',
                    p: { xs: 1, sm: 1.5 },
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                />
              )}
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  lineHeight: 1.2
                }}
              >
                {getWelcomeMessage()}
              </Typography>
            </Stack>
            
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ 
                maxWidth: 900, 
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
              }}
            >
              Manage customers, track referrals, and control access to Entrinsic
              Connect.
            </Typography>
          </Box>

          {/* Actions */}
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <ActionCard
                icon={<PeopleIcon sx={{ fontSize: 32 }} />}
                title="Customers"
                description="Create accounts, manage subscriptions and access, and provide hands-on support when needed."
                cta="Open Customers"
                onClick={() => navigate('/partner-admin/customers')}
                gradient="linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ActionCard
                icon={<Diversity3Icon sx={{ fontSize: 32 }} />}
                title="Referred Partners"
                description="View and manage your partner referrals. Track performance and help new partners succeed."
                cta="Open Referred Partners"
                onClick={() => navigate('/partner-admin/referred-partners')}
                gradient="linear-gradient(135deg, #059669 0%, #0EA5E9 100%)"
              />
            </Grid>
          </Grid>

          {/* Helpful tips */}
          <Box
            sx={{
              mt: { xs: 6, md: 8 },
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              bgcolor: 'background.paper',
              border: (theme) => `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
              Getting started
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Set up customers, assign plans, and track referred partners. Use the
              quick actions above to jump straight into what matters most.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Layout>
  );
};

export default PartnerHome;